import { Location } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import {
  ActivityService,
  LoggingService,
  TimeElapsedService,
  UrlService,
} from 'src/app/shared/services';
import { MessageService } from '../../message/services/message/message.service';
import { MessageCodes } from 'src/app/shared/enums';
import { activityIdToUuidMap } from 'src/app/data/activities/activity-id-to-uuid-map';
import { ReferrerService } from 'src/app/shared/services/referrer/referrer.service';
import { AssignmentService } from '../../olp-api/services/assignment/assignment.service';
import { FeatureFlagService } from '../../feature-flags/services/feature-flag/feature-flag.service';
import { FeatureFlags } from 'src/app/shared/enums/feature-flags';
import { Params } from '@angular/router';
import { AuthSessionService } from 'src/app/modules/auth/services/auth-session/auth-session.service';

@Component({
  templateUrl: './activity-page.component.html',
  styleUrls: ['./activity-page.component.scss'],
  standalone: false,
})
export class ActivityPageComponent implements OnInit {
  activityUuid: string | undefined;

  constructor(
    private activityService: ActivityService,
    private timeElapsedService: TimeElapsedService,
    private location: Location,
    private urlService: UrlService,
    private messageService: MessageService,
    private referrerService: ReferrerService,
    private assignmentService: AssignmentService,
    private featureFlagService: FeatureFlagService,
    private loggingService: LoggingService,
    private authSessionService: AuthSessionService
  ) {}

  ngOnInit(): void {
    this.urlService.getParams().subscribe(params => {
      // if inbox is enabled, attempt to load the expected params from the url
      if (
        this.featureFlagService.isFlagEnabled(
          FeatureFlags.ENABLE_STUDENT_INBOX
        ) &&
        !this.authSessionService.isTeacher
      ) {
        if (this.loadInboxParams(params)) {
          console.log('loadInboxParams() - Successfully loaded inbox params');
          this.loadActivity(params);
          return;
        }
      }

      // if inbox parse did not succeed or was disabled, load the traditional params
      if (this.loadUrlParams(params)) {
        console.log('loadUrlParams() - Successfully loaded url params');
        this.loadActivity(params);
        return;
      }
    });
  }

  loadUrlParams(params: Params): boolean {
    if (!params.activityId && !params.activityOid) {
      this.showLoadError();
      return false;
    }

    if (!this.parseUuid(params)) {
      this.showLoadError();
      return false;
    }

    if (Number.isNaN(Number(params.activityOid))) {
      this.showLoadError();
      return false;
    }

    // regular assignment else self-selected assignment
    if (params.assignmentId) {
      this.activityService.assignmentId = params.assignmentId;
      this.assignmentService.isAssignment.next(true);
    }

    this.activityService.currentActivityOid = Number(params.activityOid);

    return true;
  }

  parseUuid(params: Params): boolean {
    if (!params || !params.activityId) {
      return false;
    }

    const uuidRegex = new RegExp(
      '^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$'
    );

    this.activityUuid = params.activityId.toLowerCase();

    if (!uuidRegex.test(this.activityUuid ?? '')) {
      console.log('parseUuid() - Activity UUID failed the regex test');
      this.activityUuid = activityIdToUuidMap[params.activityId];
    }

    if (!this.activityUuid) {
      console.error(
        'parseUuid() - Activity UUID is invalid',
        this.activityUuid
      );
      return false;
    }

    return true;
  }

  loadInboxParams(params: Params): boolean {
    if (!this.parseUuid(params)) {
      console.error(
        'loadInboxParams() - Failed to parse Activity UUID',
        params ?? null
      );
      return false;
    }

    if (!params.inboxActivityId || !params.inboxAssignmentId) {
      // this error is ok. if this fails, then we may be in a self-selected or legacy assignment activity
      return false;
    }

    this.activityService.inboxActivityId.next(params.inboxActivityId);
    this.activityService.inboxAssignmentId = params.inboxAssignmentId;

    this.loggingService.inboxActivityId.next(params.inboxActivityId);
    this.loggingService.inboxAssignmentId.next(params.inboxAssignmentId);

    return true;
  }

  loadActivity(params: Params): void {
    if (!this.activityUuid) {
      this.showLoadError();
      return;
    }

    this.loggingService.activityUuid.next(this.activityUuid);

    const loadStartTime = Date.now();
    const restart = params.restart?.toString().toLowerCase() === 'restart';
    this.loggingService.logInfo('activity_load_start', {
      loadStartTime: loadStartTime,
      isRestart: restart,
    });

    this.activityService.fetchActivity(this.activityUuid).subscribe({
      next: () => {
        this.timeElapsedService.startTimer();

        this.loggingService.logInfo('activity_load_success', {
          loadTime: Date.now() - loadStartTime,
          isRestart: restart,
        });

        this.activityService.initActivity(
          restart ? undefined : this.activityUuid
        );
        this.location.replaceState('/');
      },
      error: err => {
        this.loggingService.logInfo('activity_load_error', {
          loadTime: Date.now() - loadStartTime,
          error: err.message || 'Unknown error',
          statusCode: err.status,
        });

        console.error(`Failed to load activity`, err);
        this.showLoadError();
      },
    });
  }

  showLoadError(): void {
    this.messageService.showMessage(MessageCodes.APP_UNKNOWN_ERROR, () => {
      this.referrerService.navigateToReferrer();
    });
  }
}
